import axios from "axios";
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Loading from "../../../Shared/Loading";
import AdminDoctorPatientTable from "./AdminDoctorPatientTable";
import { Icon } from "@iconify/react";

const AdminDoctorPatient = () => {
  const { id } = useParams();

  const [patients, setPatients] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [selected, setSelected] = useState("");
  const [filterData, setFilterData] = useState([]);

  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPatients = () => {
    fetch(
      `https://servercareseebd.careseebd.com/api/v1/user/specific?fieldName=referralId&&fieldValue=${id}`,
      {
        headers: {
          authorization: `Bearer ${localStorage.getItem("careSeeAccessToken")}`,
        },
      }
    )
      .then((res) => res.json())
      .then((data) => {
        setPatients(data?.data || []);
        setFilterData(data?.data || []);
      })
      .catch((err) => console.error(err));
  };

  useEffect(() => {
    axios
      .get(`https://servercareseebd.careseebd.com/api/v1/user/${id}`, {
        headers: {
          authorization: `Bearer ${localStorage.getItem("careSeeAccessToken")}`,
        },
      })
      .then((response) => {
        setUserData(response.data.data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });

    fetchPatients();
  }, [id]);

  const filter = patients?.filter((user) => {
    const activity = user?.isActive ? "active" : "inActive";
    return (
      (!searchInput || user?.name?.toLowerCase()?.includes(searchInput?.toLowerCase())) &&
      (!selected || selected === "all" || activity === selected)
    );
  });

  useEffect(() => {
    setFilterData(filter);
  }, [searchInput, selected, patients]);

  if (loading) {
    return <Loading />;
  }
  if (error) return <div className="p-6 text-red-500 font-bold">Error: {error}</div>;

  const activeCount = patients.filter((p) => p.isActive).length;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header Panel with Back Button */}
      <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm flex flex-col sm:flex-row justify-between sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <Link
            to="/adminDashboard/doctors"
            className="p-2.5 border border-slate-200 text-slate-500 hover:text-secondary hover:border-secondary hover:bg-slate-50 rounded-xl transition duration-150"
            title="Back to Doctors List"
          >
            <Icon icon="solar:alt-arrow-left-linear" className="text-xl" />
          </Link>
          <div>
            <h1 className="text-lg font-bold text-slate-800 tracking-tight flex items-center gap-2">
              <span>{userData?.name ? `${userData.name}'s Patients` : "Doctor's Patients"}</span>
            </h1>
            <p className="text-xs text-slate-400 font-medium mt-0.5 font-semibold">
              Manage patient registers, subscription validity, and check clinical finding records.
            </p>
          </div>
        </div>

        {/* Stats Summary Badge Row */}
        <div className="flex items-center gap-3">
          <span className="text-xs font-bold text-slate-500 bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-100 flex items-center gap-1.5">
            <Icon icon="solar:users-group-two-rounded-bold" className="text-secondary" />
            <span>Total: {patients.length}</span>
          </span>
          <span className="text-xs font-bold text-slate-500 bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-100 flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-green-550"></span>
            <span>Active: {activeCount}</span>
          </span>
        </div>
      </div>

      {/* Patients Card Directory */}
      <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-6">
        {/* Table Filters */}
        <div className="flex justify-end items-center gap-3">
          {/* Status Dropdown */}
          <div className="relative">
            <select
              onChange={(e) => setSelected(e.target.value)}
              className="appearance-none bg-slate-50 border border-slate-200 text-xs font-bold text-slate-600 pl-3 pr-8 py-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-secondary cursor-pointer"
              name="patientType"
            >
              <option value="all">All Patients</option>
              <option value="active">Active Patients</option>
              <option value="inActive">Inactive Patients</option>
            </select>
            <Icon icon="solar:alt-arrow-down-linear" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none" />
          </div>

          {/* Search Input */}
          <div className="relative min-w-[220px]">
            <Icon icon="solar:magnifer-linear" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
            <input
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 text-xs font-semibold text-slate-700 pl-9 pr-4 py-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-secondary transition placeholder:text-slate-400"
              type="search"
              name="search"
              placeholder="Search patients..."
            />
          </div>
        </div>

        {/* Patients Table */}
        <div className="overflow-x-auto rounded-xl border border-slate-100">
          <table className="w-full text-left border-collapse" cellSpacing="0">
            <thead>
              <tr className="bg-slate-50/75 border-b border-slate-100">
                <th scope="col" className="h-12 px-6 text-[10px] font-bold uppercase tracking-wider text-slate-450">
                  Name
                </th>
                <th scope="col" className="h-12 px-6 text-[10px] font-bold uppercase tracking-wider text-slate-450">
                  Email
                </th>
                <th scope="col" className="h-12 px-6 text-[10px] font-bold uppercase tracking-wider text-slate-450">
                  Phone
                </th>
                <th scope="col" className="h-12 px-6 text-[10px] font-bold uppercase tracking-wider text-slate-450">
                  Date of Birth
                </th>
                <th scope="col" className="h-12 px-6 text-[10px] font-bold uppercase tracking-wider text-slate-450">
                  Status
                </th>
                <th scope="col" className="h-12 px-6 text-[10px] font-bold uppercase tracking-wider text-slate-450">
                  Package
                </th>
                <th scope="col" className="h-12 px-6 text-[10px] font-bold uppercase tracking-wider text-slate-450 text-right">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 font-medium">
              {filterData.length > 0 ? (
                [...filterData].reverse().map((patient) => (
                  <AdminDoctorPatientTable key={patient._id} patient={patient} onUpdate={fetchPatients} />
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="h-32 text-center text-xs text-slate-400 font-semibold bg-slate-50/20">
                    No patient records found under this doctor.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminDoctorPatient;
